import { ArrowUp } from 'lucide-react';
import { useIsMutating } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useRouter } from '@uirouter/react';

import { EnvironmentId } from '@/react/portainer/environments/types';
import { HelmRelease } from '@/react/kubernetes/helm/types';

import { LoadingButton } from '@@/buttons';
import { TooltipWithChildren } from '@@/Tip/TooltipWithChildren';

import { openUpgradeHelmModal } from './UpgradeHelmModal';
import { VersionStatusMessage } from './VersionStatusMessage';

export function UpgradeButton({
  environmentId,
  namespace,
  release,
}: {
  environmentId: EnvironmentId;
  namespace: string;
  release?: HelmRelease;
}) {
  const isMutating = useRef(false);
  const router = useRouter();
  // Observe mutations to find loading state
  const isManualUpgradeMutating =
    useIsMutating({
      mutationKey: [
        'helm-release',
        'update',
        environmentId,
        release?.name,
        namespace,
      ],
    }) > 0;
  const isRedeployMutating =
    useIsMutating({
      mutationKey: ['git-stack', 'redeploy', environmentId, release?.stackID],
    }) > 0;

  useEffect(() => {
    const isLoading = isManualUpgradeMutating || isRedeployMutating;
    // when the loading state is finished, redirect to the latest revision
    if (!isLoading && isMutating.current && release) {
      router.stateService.go('kubernetes.helm', {
        namespace: release.namespace,
        name: release.name,
        revision: undefined,
      });
      router.stateService.reload();
    }
    isMutating.current = isLoading;
  }, [
    isManualUpgradeMutating,
    isRedeployMutating,
    release,
    router.stateService,
  ]);

  if (!release) {
    return null;
  }

  const isPending = release?.info?.status?.startsWith('pending');

  const upgradeButton = (
    <LoadingButton
      color="secondary"
      data-cy="k8sApp-upgradeHelmChartButton"
      onClick={openModal}
      disabled={release?.info?.status?.startsWith('pending')}
      loadingText="Upgrading..."
      isLoading={isManualUpgradeMutating || isRedeployMutating}
      icon={ArrowUp}
      size="medium"
    >
      Edit/Upgrade
    </LoadingButton>
  );

  if (isPending) {
    return (
      <div className="relative">
        <TooltipWithChildren
          message="Cannot edit/upgrade while the Helm release is pending. Please wait for the upgrade to complete, or rollback."
          position="top"
        >
          <span>{upgradeButton}</span>
        </TooltipWithChildren>
        <VersionStatusMessage release={release} />
      </div>
    );
  }

  return (
    <div className="relative">
      {upgradeButton}
      <VersionStatusMessage release={release} />
    </div>
  );

  async function openModal() {
    if (!release) {
      return;
    }

    await openUpgradeHelmModal(release);
  }
}
